import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AutoComplete, Button, Form, Input, Modal, Select, Tag } from "antd";
import { debounce } from "lodash";
import { Check } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  getExecutiveEmployeeList,
  markAsDelete,
  searchIndustry,
  selectedRow,
} from "../../actionCreator/leadListingActionCreater";
import { useAlert } from "../../context/AlertProvider";
const { TextArea } = Input;

const BulkUpdate = ({ isOpen, handleCancel }) => {
  const [clearForm, setClearForm] = useState(false);

  return (
    <Modal
      title={null}
      closable
      centered
      className="delete-popup"
      open={isOpen}
      onCancel={() => {
        handleCancel();
        setClearForm(true);
      }}
      footer={[]}
    >
      <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50">
        Mass Update
      </div>
      <LeadUpdateForm
        clearForm={clearForm}
        setClearForm={setClearForm}
        closeModal={handleCancel}
      />
    </Modal>
  );
};

export default BulkUpdate;

const LeadUpdateForm = ({ closeModal, clearForm, setClearForm }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const [industries, setIndustries] = useState([]);
  const [rating, setRating] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [status, setStatus] = useState();
  const [reason, setReason] = useState();
  const [isDirty, setIsDirty] = useState(false);
  const leadStatusListing = useSelector(
    (state) => state.leadListingReducer.leadStatusList,
  );
  const leadRatingListing = useSelector(
    (state) => state.leadListingReducer.leadRatingList,
  );
  const unqualifiedReasons = useSelector(
    (state) => state.leadListingReducer.unqualifiedReasons,
  );
  const ratingOptions = leadRatingListing.map((item) => ({
    label: item.ratingText,
    value: item.ratingText,
    color:
      item.ratingText === "Hot"
        ? "red"
        : item.ratingText === "Cold"
          ? "blue"
          : "orange",
  }));

  const selectedRecords = useSelector(
    (state) => state.leadListingReducer.selectedRecords,
  );

  const isAllowedUpdate = selectedRecords?.filter((d) => {
    return d?.executiveData?.allowedActions?.includes("EDIT");
  })?.length;

  const notAllowedUpdate = selectedRecords?.filter((d) => {
    return !d?.executiveData?.allowedActions?.includes("EDIT");
  })?.length;

  useEffect(() => {
    if (clearForm) {
      form.resetFields();
      setError();
      setRating();
      setClearForm(false);
      setIsDirty(false);
    }
  }, [clearForm, form, setClearForm]);

  const onFinish = async (values) => {
    setLoading(true);
    const cleanedObject = Object.fromEntries(
      Object.entries({
        ...values,
        industryId: values?.industryText
          ? industries?.find((d) => d?.name === values?.industryText)?.id
          : undefined,
        status: values?.status
          ? leadStatusListing?.find((i) => i?.text === values?.status)?.id
          : undefined,
        industryText: undefined,
      }).filter(([_, value]) => value != null),
    );

    const payload = selectedRecords
      ?.filter((d) => {
        return d?.executiveData?.allowedActions?.includes("EDIT");
      })
      ?.map((d) => ({
        id: d?.key,
        ...cleanedObject,
        unqualifiedReason: cleanedObject?.unqualifiedReason
          ? {
              id: unqualifiedReasons?.find(
                (d) => d?.reason === cleanedObject?.unqualifiedReason,
              )?.id,
              reason: cleanedObject?.unqualifiedReason,
            }
          : null,
      }));

    const res = await dispatch(markAsDelete({ clientLeads: payload }));
    if (res?.status === 200) {
      showAlert("success", "Leads have been successfully updated.");
      searchParams.set("page", 1);
      navigate(`${location.pathname}?${searchParams.toString()}`);
      dispatch(selectedRow([]));
      const allParams = {};
      for (const [key, value] of searchParams.entries()) {
        allParams[key] = value;
      }

      dispatch(
        getExecutiveEmployeeList({
          ...allParams,
          page: allParams?.page ? allParams?.page - 1 : 0,
        }),
      );
      closeModal();
      setIsDirty(false);
      form.resetFields();
      setRating();
    } else {
      setError(res?.response?.data?.title);
      showAlert("error", res?.response?.data?.title);
    }
    setLoading(false);
  };

  const onSearch = debounce(async (value) => {
    const res = await dispatch(searchIndustry(value));
    setIndustries(res);
  }, 500);

  return (
    <Form
      form={form}
      onFinish={onFinish}
      className="lead-form"
      layout="vertical"
      onChange={() => {
        setError();
      }}
      onValuesChange={() => {
        setError();
        setIsDirty(true);
      }}
    >
      <div className="tw-p-6">
        {error && <p className="text-error text-center pb-2">{error}</p>}
        {!!notAllowedUpdate && !!isAllowedUpdate && (
          <div className="tw-text-sm tw-font-semibold tw-mb-2">
            Note: Out of the {selectedRecords?.length} selected leads, you don’t
            have permission to edit {notAllowedUpdate} of them.
          </div>
        )}
        {!!notAllowedUpdate && !isAllowedUpdate && (
          <div className="text-error text-center pb-2">
            You don’t have permission to edit {notAllowedUpdate} selected leads.
          </div>
        )}
        <Form.Item
          label={<label className="fw-semibold">Industry</label>}
          name="industryText"
          className="mb15"
        >
          <AutoComplete
            options={industries?.map((d) => ({
              label: d.name,
              value: d.name,
            }))}
            onSearch={onSearch}
          >
            <Input
              allowClear
              placeholder="Start typing a industry name"
              className="common-input tw-py-2 tw-px-3 tw-text-[12px] tw-h-full"
            />
          </AutoComplete>
        </Form.Item>
        <Form.Item
          label={<label className="fw-semibold">Lead Status</label>}
          name="status"
          normalize={(value) => value?.replace(/^\s+/, "")}
        >
          <Select
            placeholder="Select Status"
            options={leadStatusListing?.map((d) => ({
              label: d.text,
              value: d.text,
            }))}
            onChange={(value) => {
              form.setFieldsValue({
                status: value,
              });
              setStatus(value);
            }}
          />
        </Form.Item>
        {status === "Closed Lost" && (
          <>
            <Form.Item
              name="lostReason"
              label="Lost Reason"
              rules={[{ required: true, message: "Please add a reason" }]}
            >
              <Input className="common-input" placeholder="Enter reason" />
            </Form.Item>

            <Form.Item name="comments" label="Comments">
              <TextArea
                className="common-input"
                rows={3}
                placeholder="Enter comments"
              />
            </Form.Item>
          </>
        )}

        {status === "Un-Qualified" && (
          <>
            <Form.Item
              name="unqualifiedReason"
              label="Reason for Unqualification"
              rules={[{ required: true, message: "Please select a reason" }]}
            >
              <Select
                placeholder="Select reason"
                options={unqualifiedReasons?.map((d) => ({
                  label: d.reason,
                  value: d.reason,
                }))}
                onChange={(value) => {
                  form.setFieldsValue({
                    unqualifiedReason: value,
                    unqualifiedReasonsOthers: undefined,
                  });
                  setReason(value);
                }}
              />
            </Form.Item>

            {reason === "Others" && (
              <Form.Item
                name="unqualifiedReasonsOthers"
                label="Add Reason"
                rules={[{ required: true, message: "Please add a reason" }]}
              >
                <TextArea
                  className="common-input"
                  rows={3}
                  placeholder="Enter unqualification reason"
                />
              </Form.Item>
            )}
          </>
        )}
        <Form.Item
          label={<label className="fw-semibold">Lead Source</label>}
          name="leadSource"
          normalize={(value) => value?.trimStart()}
          className="mb15"
        >
          <Input className="common-input" />
        </Form.Item>
        <Form.Item
          label={<label className="fw-semibold">Lead Rating</label>}
          name="leadRating"
          className="mb15"
        >
          {ratingOptions?.map((d, i) => (
            <Tag
              key={i}
              color={d?.color}
              className="cursor-pointer"
              onClick={() => {
                form.setFieldsValue({ leadRating: d?.value });
                setRating(d?.value);
                setError();
                setIsDirty(true);
              }}
            >
              {d?.label} {rating === d?.value && <Check size={14} className="tw-inline tw-ml-1" />}
            </Tag>
          ))}
        </Form.Item>
        <Form.Item
          label={<label className="fw-semibold">Lead Description</label>}
          name="description"
          normalize={(value) => value?.trimStart()}
          className="mb15"
        >
          <Input.TextArea rows={2} className="common-input" />
        </Form.Item>
      </div>
      <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
        <Button
          className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
          onClick={() => {
            closeModal();
            setIsDirty(false);
            form.resetFields();
            setRating();
          }}
        >
          Cancel
        </Button>

        <Button
          loading={loading}
          disabled={
            loading || !isDirty || (!!notAllowedUpdate && !isAllowedUpdate)
          }
          htmlType="submit"
          type="primary"
          className="!tw-h-[38px] !tw-rounded-lg !tw-bg-theme !tw-border-theme !tw-text-white tw-text-xs tw-font-bold tw-px-5 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-duration-200"
        >
          Save
        </Button>
      </div>
    </Form>
  );
};
